import React from 'react';
import PropTypes from 'prop-types';

import {CircularProgress, Divider, Pill, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

const ClientContext = (props) => {
  const {client, stage} = props;

  if (stage === 'LOADING') {
    return (
      <Stack direction='row' sx={{alignItems: 'center', gap: 1}}>
        <CircularProgress size={16} />
        <Typography sx={{fontSize: '12px', fontWeight: 400, color: '#6D7280'}}>
          <Translate expression='inbox.Detail.Client.Loading' />
        </Typography>
      </Stack>
    );
  }

  if (stage === 'ERROR') {
    return (
      <Typography sx={{fontSize: '12px', fontWeight: 400, color: 'error.main'}}>
        <Translate expression='inbox.Detail.Client.LoadFailed' />
      </Typography>
    );
  }

  if (!client) {
    return null;
  }

  const clientDetail = client.clientDetail || {};
  const generalInformation = client.careplan?.generalInformation || {};

  const clientName = clientDetail.clientName;
  const careRecipientName = generalInformation.clientName;
  const statusTitle = clientDetail.status ?
    translate(`client.Status.${clientDetail.status}.Title`) :
    null;
  const relationships = Object.values(clientDetail.relationships || {})
      .sort((first, second) => Number(first.priority || 0) - Number(second.priority || 0));

  const buildContactHref = (contact) => {
    const detail = String(contact?.detail || '').trim();

    if (!detail) {
      return null;
    }

    return String(contact?.type || '').toUpperCase() === 'EMAIL' ?
      `mailto:${detail}` :
      `tel:${detail.replace(/[^+\d]/g, '')}`;
  };

  return (
    <Stack sx={{gap: 1.5}}>
      <Stack direction='row' sx={{alignItems: 'baseline', justifyContent: 'space-between', gap: 1}}>
        <Typography sx={{fontSize: '15px', fontWeight: 700, color: '#050A14', wordBreak: 'break-word'}}>
          {clientName}
        </Typography>
        {
          statusTitle &&
            <Pill label={statusTitle} sx={{flexShrink: 0}} />
        }
      </Stack>

      {
        careRecipientName && careRecipientName !== clientName &&
          <Stack sx={{gap: 0.5}}>
            <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#9A9BAE'}}>
              {translate('inbox.Detail.Client.Fields.CareRecipient')}
            </Typography>
            <Typography sx={{fontSize: '13px', fontWeight: 400, color: '#050A14', wordBreak: 'break-word'}}>
              {careRecipientName}
            </Typography>
          </Stack>
      }

      {
        generalInformation.address &&
          <Stack sx={{gap: 0.5}}>
            <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#9A9BAE'}}>
              {translate('inbox.Detail.Client.Fields.Location')}
            </Typography>
            <Typography sx={{fontSize: '13px', fontWeight: 400, color: '#050A14', wordBreak: 'break-word'}}>
              {generalInformation.address}
            </Typography>
          </Stack>
      }

      {
        relationships.length > 0 &&
          <Stack sx={{gap: 0.5}}>
            <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#9A9BAE'}}>
              {translate('inbox.Detail.Client.Fields.Contacts')}
            </Typography>
            <Stack sx={{gap: 1}}>
              {
                relationships.map((relationship, index) => (
                  <Stack key={relationship.relationshipRn} sx={{gap: 0.25}}>
                    {
                      index > 0 &&
                        <Divider sx={{mb: 0.75}} />
                    }
                    <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#050A14', wordBreak: 'break-word'}}>
                      {relationship.name}
                    </Typography>
                    {
                      relationship.connectionType &&
                        <Typography sx={{fontSize: '12px', fontWeight: 400, color: '#63708A', wordBreak: 'break-word'}}>
                          {relationship.connectionType}
                        </Typography>
                    }
                    {
                      (relationship.contacts || []).map((contact) => {
                        const href = buildContactHref(contact);

                        if (!href) {
                          return null;
                        }

                        return (
                          <Typography
                            component='a'
                            href={href}
                            key={`${relationship.relationshipRn}-${contact.type}-${contact.detail}`}
                            sx={{
                              'fontSize': '12px',
                              'fontWeight': 400,
                              'color': 'primary.main',
                              'textDecoration': 'none',
                              'wordBreak': 'break-all',
                              '&:hover': {textDecoration: 'underline'},
                            }}
                          >
                            {contact.detail}
                          </Typography>
                        );
                      })
                    }
                  </Stack>
                ))
              }
            </Stack>
          </Stack>
      }
    </Stack>
  );
};

ClientContext.propTypes = {
  client: PropTypes.object,
  stage: PropTypes.string.isRequired,
};

export default React.memo(ClientContext);
